/**
 * The ONE newsletter double opt-in form (M3 flow: pending row, confirmation
 * email, consent proof, honeypot, rate limit). Used by the pre-footer panel on
 * every page and by contextual lead capture (free tools), so validation,
 * consent wording, analytics and the "check your inbox" state never drift.
 */
import { useMutation } from 'convex/react'
import { ArrowRight, Check, MailCheck } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { api } from '../../convex/_generated/api'
import { trackEvent } from '../lib/analytics'
import { anonIdCookieName } from '../lib/anonymous-state'
import { getCookie } from '../lib/cookies'
import { isValidEmail } from '../lib/email'
import { NEWSLETTER_CONSENT_VERSION, toolLeadSource } from '../lib/email-consent'
import { localizedPath, useTranslation } from '../lib/i18n'

type NewsletterStatus =
  | 'idle'
  | 'saving'
  | 'pending'
  | 'active'
  | 'invalid_email'
  | 'consent_required'
  | 'rate_limited'
  | 'error'

export function NewsletterOptIn({
  source,
  idPrefix = 'newsletter',
}: {
  /** Consent-record + analytics source, e.g. `pre_footer` or `tool_<slug>`. */
  source: string
  /** Unique per page: input ids are `${idPrefix}-name` / `${idPrefix}-email`. */
  idPrefix?: string
}) {
  const { t, locale } = useTranslation()
  const subscribe = useMutation(api.newsletter.subscribe)
  const [status, setStatus] = useState<NewsletterStatus>('idle')
  const [sentTo, setSentTo] = useState('')
  const inboxRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (status === 'pending' || status === 'active') inboxRef.current?.focus()
  }, [status])

  async function onSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault()
    const form = event.currentTarget
    const data = new FormData(form)
    const email = String(data.get('email') ?? '')
    if (!isValidEmail(email)) {
      setStatus('invalid_email')
      return
    }
    if (data.get('consent') !== 'on') {
      setStatus('consent_required')
      return
    }
    setStatus('saving')
    try {
      const result = await subscribe({
        email,
        name: String(data.get('name') ?? '') || undefined,
        source,
        locale,
        consent: true,
        consentTextVersion: NEWSLETTER_CONSENT_VERSION,
        anonId: getCookie(anonIdCookieName) ?? undefined,
        website: String(data.get('website') ?? '') || undefined,
      })
      setStatus(result.status)
      if (result.status === 'pending' || result.status === 'active') {
        setSentTo(email.trim())
        // No PII in analytics: only where it happened and the outcome.
        trackEvent('newsletter_signup_requested', {
          source,
          status: result.status,
        })
        form.reset()
      }
    } catch {
      setStatus('error')
    }
  }

  const done = status === 'pending' || status === 'active'
  const errorMessage: Partial<Record<NewsletterStatus, string>> = {
    invalid_email: t('pricing.founding.invalidEmail'),
    consent_required: t('pricing.founding.consentRequired'),
    rate_limited: t('pricing.founding.rateLimited'),
    error: t('forms.genericError'),
  }

  return (
    <>
      {done ? (
        <div
          className="newsletter-inbox"
          ref={inboxRef}
          tabIndex={-1}
          role="status"
          aria-live="polite"
          data-newsletter-state={status}
        >
          <span className="newsletter-inbox-icon" aria-hidden="true">
            <MailCheck size={26} strokeWidth={2.2} />
          </span>
          <h3>
            {status === 'active'
              ? t('email.confirm.newsletterTitle')
              : t('email.inbox.title')}
          </h3>
          <p>
            {status === 'active'
              ? t('email.confirm.newsletterText')
              : t('email.inbox.body', { email: sentTo })}
          </p>
          <button
            type="button"
            className="ghost-button newsletter-inbox-reset"
            onClick={() => setStatus('idle')}
          >
            {t('email.inbox.again')}
          </button>
        </div>
      ) : (
      <form className="newsletter-form" onSubmit={onSubmit} noValidate>
        <div className="newsletter-field">
          <label htmlFor={`${idPrefix}-name`}>
            {t('newsletter.nameLabel')}
          </label>
          <input
            id={`${idPrefix}-name`}
            name="name"
            placeholder={t('newsletter.namePlaceholder')}
            autoComplete="given-name"
          />
        </div>
        <div className="newsletter-field">
          <label htmlFor={`${idPrefix}-email`}>
            {t('newsletter.emailLabel')}
          </label>
          <input
            id={`${idPrefix}-email`}
            type="email"
            name="email"
            placeholder={t('newsletter.emailPlaceholder')}
            autoComplete="email"
            required
          />
        </div>
        <input
          className="hp-field"
          name="website"
          tabIndex={-1}
          autoComplete="off"
          aria-hidden="true"
        />
        <label className="newsletter-consent">
          <input type="checkbox" name="consent" required />
          <span className="newsletter-check" aria-hidden="true">
            <Check size={14} strokeWidth={3} />
          </span>
          <span className="newsletter-consent-text">
            {t('newsletter.consentPrefix')}{' '}
            <a href={localizedPath('privacy', locale)}>
              {t('newsletter.consentLink')}
            </a>
            .
          </span>
        </label>
        <button
          className="primary-button is-accent newsletter-submit"
          type="submit"
          disabled={status === 'saving'}
        >
          {status === 'saving'
            ? t('common.sending')
            : t('newsletter.submit')}
          <ArrowRight size={18} />
        </button>
        {errorMessage[status] ? (
          <p className="form-status is-error" role="status">
            {errorMessage[status]}
          </p>
        ) : null}
      </form>
      )}
    </>
  )
}

/**
 * Contextual lead capture under a free tool (M4): the moment someone just got
 * value is the moment they are most willing to hear from us. Same double
 * opt-in as everywhere, tagged `tool_<slug>` so we can see which tool converts.
 * On tool pages it replaces the generic pre-footer panel (no second form).
 */
export function ToolLeadCapture({
  toolSlug,
  toolTitle,
}: {
  toolSlug: string
  toolTitle: string
}) {
  const { t, dict } = useTranslation()
  const ref = useRef<HTMLElement | null>(null)
  useEffect(() => {
    const node = ref.current
    if (!node || typeof IntersectionObserver === 'undefined') return
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          trackEvent('upsell_viewed', { surface: 'tool_lead_capture', lesson: toolSlug })
          observer.disconnect()
        }
      },
      { threshold: 0.5 },
    )
    observer.observe(node)
    return () => observer.disconnect()
  }, [toolSlug])

  return (
    <section
      ref={ref}
      className="tool-lead home-newsletter-section"
      aria-label={t('toolLead.eyebrow')}
      data-tool-lead={toolSlug}
    >
      <div className="newsletter-card">
        <div className="newsletter-card-glow" aria-hidden="true" />
        <div className="newsletter-card-grid">
          <div className="newsletter-copy">
            <span className="eyebrow newsletter-eyebrow">{t('toolLead.eyebrow')}</span>
            <h2>{t('toolLead.title', { tool: toolTitle })}</h2>
            <p>{t('toolLead.text')}</p>
            <ul className="newsletter-perks">
              {dict.newsletter.perks.slice(1).map((perk) => (
                <li key={perk}>
                  <span className="newsletter-perk-icon" aria-hidden="true">
                    <Check size={14} strokeWidth={3} />
                  </span>
                  <span>{perk}</span>
                </li>
              ))}
            </ul>
          </div>
          <NewsletterOptIn source={toolLeadSource(toolSlug)} idPrefix="tool-newsletter" />
        </div>
      </div>
    </section>
  )
}
